import { SupportedLanguages } from '~/config/i18n'
import { UtmFields } from '~/utils/utm-fields'
import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { action as emailCheckAction } from '~/routes/email-check'
import { useFetcher } from '@remix-run/react'
import { debounce } from 'lodash-es'
import { trialText } from '~/config'
import { Select } from './select'
import { countries as countryList } from 'countries-list'
import { leadSource, leadSourceReferral } from '~/components/forms/options'

declare const window: Window & {
  lintrk: (str: string, data: Record<string, unknown>) => void
}

type Props = {
  /**
   * @default 'Request Free Trial'
   */
  buttonLabel?: string
  /**
   * @default en
   */
  lang?: SupportedLanguages
  children?: ReactNode
}

type FormLabelTranslation = {
  firstName: string
  lastName: string
  email: string
  company: string
  headquarters: string
}

const labelsLang: Record<string, FormLabelTranslation> = {
  en: {
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Work email',
    company: 'Company name',
    headquarters: 'Headquarters',
  },
  fr: {
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'E-mail',
    company: "Nom de l'entreprise",
    headquarters: 'Siège social',
  },
}

export const FormBasics = ({
  buttonLabel = 'Request Free Trial',
  lang = SupportedLanguages.English,
  children,
}: Props) => {
  const countries = Object.values(countryList)
    .map(({ name }) => name)
    .sort()

  const [source, setSource] = useState('')
  const handleSetSource = (event: ChangeEvent<HTMLSelectElement>) =>
    setSource(event.target.value)

  const labels = labelsLang[lang]

  const fetcher = useFetcher<typeof emailCheckAction>()
  const isDone = fetcher.state === 'idle' && fetcher.data != null

  const [lastCheckedEmail, setLastCheckedEmail] = useState('')

  const canSubmit = isDone && fetcher.data?.valid

  const emailInputRef = useRef<HTMLInputElement>(null)
  const onEmailBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const email = event.target.value
    if (email === lastCheckedEmail) return
    fetcher.submit({ email }, { action: '/email-check', method: 'post' })
    setLastCheckedEmail(email)
  }

  const onEmailChange = useMemo(() => debounce(onEmailBlur, 1000), [])

  useEffect(() => {
    if (!emailInputRef.current) return
    emailInputRef.current.setCustomValidity(fetcher.data?.message ?? '')
  }, [fetcher.data])

  const onSubmit = () => {
    if (!canSubmit) return
    const data = { conversion_id: trialText.conversionId }
    window.lintrk('track', data)
  }

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      action={
        canSubmit ? 'https://exploreomni.activehosted.com/proc.php' : undefined
      }
    >
      <input type="hidden" name="u" value="17" />
      <input type="hidden" name="f" value="17" />
      <input type="hidden" name="s" />
      <input type="hidden" name="c" value="0" />
      <input type="hidden" name="m" value="0" />
      <input type="hidden" name="act" value="sub" />
      <input type="hidden" name="v" value="2" />
      <input type="hidden" name="or" value="2b1605e456049c2cbc9461d53743ba0b" />
      <UtmFields />
      <fieldset className="choices-columns">
        <div className="field">
          <label htmlFor="firstname">
            {labels.firstName} <span className="required">*</span>
          </label>
          <input type="text" name="firstname" required />
        </div>
        <div className="field">
          <label htmlFor="lastname">
            {labels.lastName} <span className="required">*</span>
          </label>
          <input type="text" name="lastname" required />
        </div>
      </fieldset>

      <div className="field">
        <label htmlFor="email">
          {labels.email} <span className="required">*</span>
        </label>
        <input
          type="email"
          name="email"
          size={32}
          required
          onBlur={onEmailBlur}
          onChange={onEmailChange}
          ref={emailInputRef}
        />
        {fetcher.data?.message && (
          <div className="validation-error">{fetcher.data?.message}</div>
        )}
      </div>
      <div className="field">
        <label htmlFor="customer_account">
          {labels.company} <span className="required">*</span>
        </label>
        <input type="text" size={42} name="customer_account" required />
      </div>

      <div className="field">
        <label htmlFor="field[11]">
          {labels.headquarters} <span className="required">*</span>
        </label>
        <select name="field[11]" required defaultValue="United States">
          {countries.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="choices">
        <legend>
          How did you hear about us? <span className="required">*</span>
        </legend>
        <Select
          includeBlank
          choices={leadSource}
          // This causes react errors. Stop it.
          // choices={shuffle([...leadSource])}
          onChange={handleSetSource}
          name="field[43]"
          required
          value={source}
        />
      </fieldset>

      {source === leadSourceReferral && (
        <div className="field">
          <label htmlFor="field[53]">
            Who referred you to us? <span className="required">*</span>
          </label>
          <input type="text" size={42} name="field[53]" required />
        </div>
      )}

      {children}

      <button type="submit">{buttonLabel}</button>
    </form>
  )
}
